import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
    ActionStatus, GamePlayerStatus,
} from '../../../Constants';
import { OVERLAYS_URL } from '../../../Settings';
import {
    ghostedDocks,
    playersWithGhostableDocks,
} from '../../Game/GamePlayerInfo';

import { ActionAttack } from './ActionAttack';
import { ActionClockout } from './ActionClockout';
import { ActionWrapper } from './ActionWrapper';

import './GhostActionWindow.css';

// Ghost-themed config for the shared Dashboard primitives.
// GHOST_TEXTURE_ID (76) is the per-fate-slot icon; GhostJuiceOverlay
// is the background art for the juice bar. The target picker uses
// the generic TargetingOverlay (ActionAttack's default), which is
// what GhostingController's GhostActionTarget also uses.
const GHOST_TEXTURE_ID = 76;
const GHOST_JUICE_OVERLAY = OVERLAYS_URL
    + 'GhostJuiceOverlay-1080x210.png';

function computeMode(gamePlayerData) {
    if (!gamePlayerData) return null;
    if (gamePlayerData.status !== GamePlayerStatus.GHOSTING_ID) {
        return null;
    }
    const actionStatus = gamePlayerData.action_status;
    if (actionStatus === ActionStatus.WAIT_FOR_TARGET_ID) return 'target';
    if (actionStatus === ActionStatus.WAIT_FOR_JUICE) return 'juice';
    return null;
}

// GhostActionWindow — Ghost HUD for the Desktop preset. Uses the
// shared Dashboard primitives (ActionClockout, ActionAttack)
// parametrized with ghost-themed overlays and texture so the surface
// matches mobile's GhostActionTarget / GhostActionJuice without the
// receivedMessage coupling those class components have to the
// mobile ChoiceRouter.
//
// The window owns only the target picker (WAIT_FOR_TARGET_ID) and
// juice/Confirm bar (WAIT_FOR_JUICE). Self-cleanse dock picking at
// REMOVE_ID lives on the player board via RemoveGhosts2d; opponent
// dock highlights at GHOSTED_ID live on the target's board via
// AddGhost2d. ActionInventory is NOT rendered — it's a lodging-
// themed primitive (renders LodgingOverlay + lodging slots) and the
// 2D ghost surface has no inventory analogue.
//
// Engine routes the post-juice spend in ghost_action._dispatch_spend
// (self → prompt_to_remove_ghosts, opp → apply_attack) based on the
// persisted current_action_target_id. The window sends only the
// sold_robots payload via api.ghostJuice; the engine handles the
// rest.
export function GhostActionWindow(props) {
    const { gamePlayerData, gameData, app } = props;

    const status = gamePlayerData ? gamePlayerData.status : null;
    const actionStatus = gamePlayerData
        ? gamePlayerData.action_status : null;

    const [soldRobots, setSoldRobots] = useState([]);

    const soldRobotsRef = useRef(soldRobots);
    useEffect(() => { soldRobotsRef.current = soldRobots; },
        [soldRobots]);

    useEffect(() => {
        setSoldRobots([]);
    }, [status, actionStatus]);

    const mode = computeMode(gamePlayerData);

    const isSelfTarget = (
        !gamePlayerData
        || gamePlayerData.current_action_target == null
        || gamePlayerData.current_action_target === gamePlayerData.id);

    const api = (app && app.game && app.game.seatedController
        && app.game.seatedController.gamePlayerApi)
        ? app.game.seatedController.gamePlayerApi : null;

    const onClockOut = useCallback((robot) => {
        setSoldRobots((prev) => [...prev, robot]);
    }, []);

    const onReset = useCallback(() => {
        setSoldRobots([]);
    }, []);

    const onCommit = useCallback((soldList) => {
        if (!api) return;
        if (mode === 'juice') api.ghostJuice(soldList);
    }, [api, mode]);

    const onTargetClick = useCallback((playerId) => {
        if (!api) return;
        if (mode === 'target') api.ghostCommitTarget(playerId);
    }, [api, mode]);

    const onDockClick = useCallback((dock) => {
        if (!api) return;
        if (!gamePlayerData) return;
        api.ghostFullAction(
            soldRobotsRef.current,
            gamePlayerData.id,
            dock);
    }, [api, gamePlayerData]);

    // Additive second input path for the Manual §7.2.6 target-pick
    // step: light up the eligible competitor boards inside the
    // Competitors floating window. Mirrors the mobile
    // GhostActionTarget.componentDidMount's
    // highlight-competitors-event dispatch. The ActionAttack swiper
    // below keeps working untouched — second affordance, not
    // replacement. Candidate set is the SAME picker the swiper uses
    // (playersWithGhostableDocks: self when it can cleanse, plus
    // attackable opponents). Bail without dispatching when the set is
    // empty, matching the producer guards elsewhere.
    useEffect(() => {
        if (mode !== 'target') return undefined;
        if (!gameData || !gamePlayerData) return undefined;
        const candidates = playersWithGhostableDocks(
            gameData, gamePlayerData);
        const playerIds = candidates.map((gp) => gp.id);
        if (playerIds.length === 0) return undefined;
        window.dispatchEvent(new CustomEvent(
            'highlight-competitors-event', {
                detail: {
                    playerIds: playerIds,
                    callback: onTargetClick,
                },
            }));
        return () => {
            window.dispatchEvent(
                new Event('clear-competitors-highlight-event'));
        };
    }, [mode, gameData, gamePlayerData, onTargetClick]);

    // Mobile parity: GhostActionJuice (GhostingController.js
    // componentDidMount → highlightDocks) dispatches
    // highlight-docks-event whenever its mount lands at
    // WAIT_FOR_JUICE with a self target, so a dock click on the
    // player board fires ghostFullAction(soldRobots, my_id, dock).
    // The engine's prompt_to_remove_ghosts inline shortcut consumes
    // dock + sold_robots in the same package, fusing juice with
    // first deghost. Desktop's GhostActionWindow has no equivalent;
    // without this dispatch the player has no commit affordance
    // (ActionClockout's Confirm button is gated on !isSelfTarget,
    // Skip on currentFate === 0). Bail on opp-target (engine ignores
    // dock for opp spends and routes to apply_attack — clicking a
    // dock would mislead). Bail on empty ghostedDocks (defensive,
    // matching producer guards in mobile GhostActionJuice via the
    // clearDocks branch).
    useEffect(() => {
        if (mode !== 'juice') return undefined;
        if (!isSelfTarget) return undefined;
        if (!gamePlayerData) return undefined;
        const docks = ghostedDocks(gamePlayerData);
        if (docks.length === 0) return undefined;
        window.dispatchEvent(new CustomEvent(
            'highlight-docks-event', {
                detail: {
                    highlightedDocks: docks,
                    callback: onDockClick,
                },
            }));
        return () => {
            window.dispatchEvent(
                new Event('clear-docks-highlight-event'));
        };
    }, [mode, isSelfTarget, gamePlayerData, onDockClick]);

    const wrapperClasses = [
        'ghost-action-window',
        mode ? 'ghost-action-window--' + mode
             : 'ghost-action-window--idle',
    ].join(' ');

    return (
        <div className={wrapperClasses}
             data-testid={'ghost-action-window'}
             data-mode={mode || 'idle'}>
            {mode && (
                <ActionWrapper>
                    {mode === 'juice' && (
                        <ActionClockout
                            gamePlayerData={gamePlayerData}
                            mode={mode}
                            soldRobots={soldRobots}
                            isSelfTarget={isSelfTarget}
                            actionTextureId={GHOST_TEXTURE_ID}
                            overlay={GHOST_JUICE_OVERLAY}
                            onClockOut={onClockOut}
                            onReset={onReset}
                            onCommit={onCommit}/>
                    )}
                    {mode === 'target' && (
                        <ActionAttack
                            gamePlayerData={gamePlayerData}
                            gameData={gameData}
                            pickCandidates={playersWithGhostableDocks}
                            onTargetClick={onTargetClick}/>
                    )}
                </ActionWrapper>
            )}
        </div>
    );
}

export default GhostActionWindow;
